import { gql } from "generated-graphql";
import {
  ManifestReviewDiscrepanciesQuery,
  StaffManifestReviewManifestsQuery,
} from "generated-graphql/graphql";
import { type DiscrepancySectionKey } from "routes/Customer/Waste/Manifests/DiscrepancyReviewDialog";

export const STAFF_MANIFEST_REVIEW_MANIFESTS_QUERY = gql(`
  query StaffManifestReviewManifests($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!], $completionState: String, $processedManifestId: ID) {
    staffManifestReviewManifests(search: $search, page: $page, pageSize: $pageSize, sort: $sort, completionState: $completionState, processedManifestId: $processedManifestId) {
      totalCount
      items {
        id
        processedManifestId
        tenantId
        tenantName
        facilityName
        facilityEpaId
        manifestTrackingNumber
        discrepancyCount
        unresolvedDiscrepancyCount
        pendingDiscrepancyCount
        encampUnresolved
        encampPending
        encampResolved
        dataQualityUnresolved
        dataQualityPending
        dataQualityResolved
        epaUnresolved
        epaPending
        epaResolved
        hasDocumentReview
        documentReviewPending
        documentReviewState
        availableSources
        status
        blockedReason
        blockedNote
        blockedAt
        blockedBy {
          id
          email
          person {
            first
            last
          }
        }
        eManifestComplianceEscalation
        daysSinceShipment
        shippedDate
        isUniformHazardousManifest
        isEManifest
        transporterContactedAt
        exceptionReportSubmissionStatus
        assignee {
          id
          email
          person {
            first
            last
          }
        }
        assignedBy {
          id
          email
          person {
            first
            last
          }
        }
        assignedAt
        documentIds
        completedAt
        completedBy
        completedSummary
        commentCount
      }
    }
  }
`);

export const MANIFEST_REVIEW_DISCREPANCIES_QUERY = gql(`
  query ManifestReviewDiscrepancies($processedManifestId: ID!) {
    manifestDiscrepancies(processedManifestId: $processedManifestId) {
      id
      field
      kind
      ruleKey
      escalation
      status
      severity
      eManifestValue
      vendorValue
      manifestDocumentValue
      resolvedAt
      resolutionPath
      resolutionNote
      wasteManifestCorrectionRequest {
        id
        requestDate
        initiatorRole
      }
    }
  }
`);

// Assignment is manifest-scoped: the server resolves each manifest's CURRENT active
// items (discrepancies + document review) at write time. Sending item ids captured at
// page load instead would fail whenever those items churned underneath an open tab
// (resolved elsewhere, EPA-correction rows deleted, re-seeds) — the "reassign does
// nothing, old assignee remains" bug.
export const ASSIGN_MANIFEST_REVIEW_MANIFESTS_MUTATION = gql(`
  mutation AssignManifestReviewManifestsFromQueue($processedManifestIds: [ID!]!, $staffUserId: ID) {
    assignManifestReviewManifests(processedManifestIds: $processedManifestIds, staffUserId: $staffUserId) {
      assignedDiscrepancyCount
      assignedDocumentReviewCount
    }
  }
`);

export type ManifestReviewRow =
  StaffManifestReviewManifestsQuery["staffManifestReviewManifests"]["items"][number];

export type ManifestReviewDiscrepancy =
  ManifestReviewDiscrepanciesQuery["manifestDiscrepancies"][number];

/** Active discrepancy: not yet resolved or dismissed (mirrors the queue's count). */
export function isActiveDiscrepancy(d: ManifestReviewDiscrepancy): boolean {
  return d.status !== "RESOLVED" && d.status !== "IGNORED";
}

/**
 * Customer-visible discrepancy: anything not dismissed. Customers see rows at
 * every lifecycle status, including not-yet-reviewed ones — telling them a
 * manifest is clean and then surfacing resolved rows later reads as data
 * appearing from nowhere. IGNORED rows are dismissed non-discrepancies and
 * render for no one. Which *kinds* reach a customer is the caller's decision
 * (the modal keeps missing-e-Manifest staff-only).
 */
export function isCustomerVisibleDiscrepancy(d: ManifestReviewDiscrepancy): boolean {
  return d.status !== "IGNORED";
}

/**
 * An in-place "quick link" opened from a queue row instead of navigating to the
 * customer manifest route: the manifest detail drawer, the document-review modal,
 * or the discrepancy dialog on a specific tab. Tenant is carried explicitly since
 * the staff queue is cross-tenant and off any tenant-scoped route.
 */
export type ManifestQuickLink =
  | { kind: "drawer"; processedManifestId: string; tenantId: string }
  | {
      kind: "docReview";
      processedManifestId: string;
      tenantId: string;
      /**
       * Needs-review document ids already on the row. When present we open the
       * document-review modal directly with these, skipping a heavy (uncached)
       * processed-manifest fetch just to resolve the source document.
       */
      documentIds: string[];
    }
  | {
      kind: "discrepancies";
      processedManifestId: string;
      tenantId: string;
      tab: DiscrepancySectionKey;
      /** For the dialog title — without it the dialog falls back to a truncated UUID. */
      manifestTrackingNumber: string | null;
    };

/** Display name for an assignee (person name, falling back to email). */
export function assigneeLabel(assignee: ManifestReviewRow["assignee"]): string {
  if (!assignee) return "Unassigned";
  const name = assignee.person
    ? `${assignee.person.first ?? ""} ${assignee.person.last ?? ""}`.trim()
    : "";
  return name || assignee.email;
}
